import { FC } from 'react';
import { Modal, Form, Row, Col, Input, Select, Divider, Checkbox } from 'antd';
import { useApp } from '@/App';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
	getAllUsers,
	getAllRoles,
	createUser,
	CreateUserPayload,
	AdminDivisionData,
} from '@/libs/fleet-api/services/admin';
import { useAsyncModal } from '@/libs/antd/components/Modal';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import formRule from '@/libs/antd/rules';

export const useCreateUserModal = () => {
	return useAsyncModal<undefined, CreateUserPayload>();
};

export type CreateUserModalProps = {
	modal: ReturnType<typeof useCreateUserModal>['modal'] | null;
	divisions?: AdminDivisionData[];
	onClose: () => void;
};

const CreateUserModal: FC<CreateUserModalProps> = ({
	modal,
	divisions = [],
	onClose,
}) => {
	const { message } = useApp();
	const queryClient = useQueryClient();
	const { form } = useForm<CreateUserPayload>();

	const { data: roles } = useQuery({
		queryKey: ['admin-roles'],
		queryFn: getAllRoles,
	});

	const { data: users } = useQuery({
		queryKey: ['admin-users'],
		queryFn: getAllUsers,
	});

	const createUserMutation = useMutation({
		mutationFn: createUser,
		onSuccess: (result) => {
			queryClient.invalidateQueries({ queryKey: ['admin-users'] });
			message.success(`Pengguna ${result.data.userId} berhasil ditambahkan`);
			onClose();
		},
		onError: (error: unknown) => {
			message.error('Gagal menambahkan pengguna');
			console.error(error);
		},
	});

	const handleSubmit = async (values: unknown) => {
		createUserMutation.mutate(values as CreateUserPayload);
	};

	if (!modal?.open) return null;

	const availableManagers =
		users?.data?.filter(
			(user) =>
				user.isActive &&
				user.group.code.toLocaleLowerCase() !== 'frm' &&
				user.group.code.toLocaleLowerCase() !== 'cca',
		) || [];

	return (
		<Modal
			{...modal}
			title="Tambah Pengguna Baru"
			okText="Tambah"
			confirmLoading={createUserMutation.isPending}
			onOk={() => form.submit()}
			width={600}
		>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				layout="horizontal"
				onFinish={handleSubmit}
			>
				<Row gutter={[40, 20]}>
					<Col span={24}>
						<Form.Item
							name="username"
							label="Username"
							rules={[
								formRule.required('Username wajib diisi'),
								{
									validator: (_, value) => {
										if (
											value &&
											users?.data?.some(
												(user) =>
													user.username.toLowerCase() === value.toLowerCase(),
											)
										) {
											return Promise.reject('Username sudah digunakan');
										}
										return Promise.resolve();
									},
								},
							]}
						>
							<Input placeholder="Masukkan username unik" />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name="fullName"
							label="Nama Lengkap"
							rules={[formRule.required('Nama lengkap wajib diisi')]}
						>
							<Input placeholder="Masukkan nama lengkap" />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name="email"
							label="Email"
							rules={[
								formRule.required('Email wajib diisi'),
								formRule.email('Format email tidak valid'),
								{
									validator: (_, value) => {
										if (
											value &&
											users?.data?.some(
												(user) =>
													user.email.toLowerCase() === value.toLowerCase(),
											)
										) {
											return Promise.reject('Email sudah digunakan');
										}
										return Promise.resolve();
									},
								},
							]}
						>
							<Input placeholder="Masukkan alamat email" />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name="groupId"
							label="Role"
							rules={[formRule.required('Role wajib dipilih')]}
						>
							<Select
								placeholder="Pilih role pengguna"
								options={roles?.data?.map((role) => ({
									label: role.name,
									value: role.id,
								}))}
							/>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name="divisionId"
							label="Divisi"
						>
							<Select
								placeholder="Pilih divisi (opsional)"
								allowClear
								options={divisions?.map((division) => ({
									label: division.name,
									value: division.id,
								}))}
							/>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name="reportToId"
							label="Atasan"
						>
							<Select
								placeholder="Pilih atasan (opsional)"
								allowClear
								options={availableManagers?.map((manager) => ({
									label: `${manager.fullName} (${manager.group.name})`,
									value: manager.userId,
								}))}
							/>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name="isExternalUser"
							label="Pengguna Eksternal"
							valuePropName="checked"
						>
							<Checkbox>User ESTAR</Checkbox>
						</Form.Item>
					</Col>
				</Row>
				<Divider style={{ margin: '40px 0' }} />
			</Form>
		</Modal>
	);
};

export default CreateUserModal;
